/**
 * RHYTHM — 24 hours across, 7 weekdays down. 168 cells, always.
 *
 * This is the panel a manager screenshots and takes into a staffing
 * conversation, which is why it gets the most care on the screen.
 *
 * ── NOT RECHARTS, AND THE REASON IS NOT TASTE ──
 * 168 `<Cell>` elements inside a recharts scatter mount 168 SVG nodes through a
 * pipeline whose whole job is computing scales this grid does not have: both
 * axes are fixed positions known before any data arrives. Worse, three things
 * this panel needs are awkward or impossible inside the chart — a visible
 * boundary on an empty cell, a real focus target per cell, and a per-cell
 * accessible name. A CSS grid gives all three and costs 168 divs.
 * `dashboard-screen/chart-sizing.test.ts` asserts it stays a grid.
 *
 * ── THE FILL IS THE SEQUENTIAL RAMP, AND THE LEGEND IS A GRADIENT ──
 * `dataViz.sequential` is validated as a CONTINUOUS FILL ONLY: its first step is
 * ΔL 0.051 from the second against a 0.06 floor, and its palest step is 1.10:1
 * against the surface. So the legend is a gradient strip with its two ends
 * labelled — a row of seven chips would show two steps nobody can separate and
 * one nobody can see.
 *
 * ── EVERY EMPTY CELL IS DRAWN ──
 * A dead hour being visible is the entire value of the panel. An omitted cell
 * renders as a hole, and a hole reads as missing data rather than as "nobody
 * logged anything then" — which is the answer somebody is looking for when they
 * ask whether the 03:00 slot needs covering.
 *
 * ── AND IT IS NOT A SHIFT-HOURS REPORT ──
 * The dimension key on the response is `hourCount`, not `hours`, deliberately.
 * Nothing in this file says "hours worked", and nothing in it counts time. It
 * counts RECORDS, hour by hour.
 */
import React, { useMemo, useRef, useState } from 'react';
import { AnalyticsRhythm, AnalyticsRhythmCell } from '../../../../../api/analytics-client';
import { PanelShell, ChartShellState, ScrollableChart } from '../charts/chart-shell';
import { heatTone } from '../charts/chart-palette';
import { TipCard, TipLabel, TipNote, TipRow, TipStrong } from '../charts/chart.styled';
import { format } from '../charts/chart-theme';
import {
  HeatCell,
  HeatCorner,
  HeatDayLabel,
  HeatGrid,
  HeatHourLabel,
  HeatLegend,
  HeatLegendBar,
  HeatLegendLabel,
  HeatRow,
  HeatTip,
  HeatWrap,
  MeterMeta,
  PanelSection,
  PanelSectionHead,
  PanelSectionTitle,
  SectionCaption,
  StatePill,
} from './panels.styled';

const PANEL_ID = 'rhythm';

/** 00 -> "00:00". Two digits, because "0:00" and "00:00" in the same axis row
 *  read as different label widths and the column stops lining up. */
const hourLabel = (hour: number): string => `${String(hour).padStart(2, '0')}:00`;

interface Anchor {
  weekday: number;
  hour: number;
  value: number;
  left: number;
  top: number;
}

export interface RhythmPanelProps {
  state: ChartShellState;
  data?: AnalyticsRhythm;
  /** Clicking a cell narrows to that weekday AND that hour. */
  onCell?: (weekday: number, hour: number) => void;
  onExportCsv?: () => void;
}

export const RhythmPanel: React.FC<RhythmPanelProps> = ({
  state,
  data,
  onCell,
  onExportCsv,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  /* ONE TAB STOP FOR 168 CELLS. A grid where every cell is tabbable puts 168
     stops between the panel above it and the panel below, which is the point at
     which a keyboard user stops using the page. The roving pattern gives the
     grid a single stop and hands the arrow keys the job of moving inside it. */
  const [cursor, setCursor] = useState<{ row: number; col: number }>({ row: 0, col: 0 });

  /* NO GENERIC CROSS-ZONE CAPTION HERE, and that is deliberate rather than an
     omission. This is the ONE route on the hub that pushes its OWN cross-zone
     sentence — "a single cell holds each building's OWN 14:00 rather than one
     instant" — whenever zones.length > 1. The generic caption beside it printed
     two sentences about timezones in a row, and the first browser pass showed
     them reading as a contradiction rather than as two facts. */
  const extra: string[] = [];
  if (data) {
    const expected = data.weekdays.length * data.hourCount;
    if (data.cells.length !== expected) {
      // The server zero-fills all 168 by construction, so this can only fire if a
      // response was truncated somewhere. Stated rather than papered over with
      // invented zeros: a missing cell is not a quiet hour.
      extra.push(
        `The grid should hold ${expected} cells and this answer carries ` +
          `${data.cells.length}. The missing positions are drawn as absent rather ` +
          'than as zero, because a cell that did not arrive is not a quiet hour.'
      );
    }
  }
  const notes = data
    ? [
        ...data.notes.filter(note => !data.scope?.note || note !== data.scope.note),
        ...extra,
      ]
    : [];

  /* (weekday, hour) -> cell. Built from what the server actually sent, so a
     position with no cell renders as ABSENT rather than as a zero this screen
     invented. */
  const byPosition = useMemo(() => {
    const map = new Map<string, AnalyticsRhythmCell>();
    for (const cell of data?.cells || []) map.set(`${cell.weekday}-${cell.hour}`, cell);

    return map;
  }, [data]);

  const weekdays = data?.weekdays || [];
  const hours = useMemo(
    () => Array.from({ length: data?.hourCount || 0 }, (_, hour) => hour),
    [data?.hourCount]
  );

  const focusCell = (row: number, col: number) => {
    const el = wrapRef.current?.querySelector<HTMLElement>(`[data-rc="${row}-${col}"]`);
    if (el) el.focus();
  };

  /* ARROW KEYS MOVE FOCUS; FOCUS MOVES THE CURSOR. The DOM for all 168 cells is
     already mounted, so the next cell can be focused synchronously here and its
     own onFocus is what updates the roving stop — one direction of data flow
     instead of a state change racing a focus call. */
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const { row, col } = cursor;
    const lastRow = weekdays.length - 1;
    const lastCol = hours.length - 1;
    let next: { row: number; col: number } | null = null;

    if (event.key === 'ArrowRight') next = { row, col: Math.min(lastCol, col + 1) };
    else if (event.key === 'ArrowLeft') next = { row, col: Math.max(0, col - 1) };
    else if (event.key === 'ArrowDown') next = { row: Math.min(lastRow, row + 1), col };
    else if (event.key === 'ArrowUp') next = { row: Math.max(0, row - 1), col };
    else if (event.key === 'Home') next = { row, col: 0 };
    else if (event.key === 'End') next = { row, col: lastCol };
    if (!next) return;

    event.preventDefault();
    focusCell(next.row, next.col);
  };

  const activate = (
    element: HTMLElement,
    row: number,
    col: number,
    cell: AnalyticsRhythmCell | undefined,
    weekday: number
  ) => {
    setCursor({ row, col });
    /* CLAMPED INSIDE THE WRAPPER. The readout is 140-260px wide and centred on
       the cell, so on the 00:00 and 23:00 columns half of it would be outside
       the scroller and clipped — the one place a tooltip is useless. */
    const wrapWidth = wrapRef.current?.offsetWidth || 0;
    const centre = element.offsetLeft + element.offsetWidth / 2;
    setAnchor({
      weekday,
      hour: col,
      value: cell ? cell.value : 0,
      left: Math.max(80, Math.min(Math.max(80, wrapWidth - 80), centre)),
      top: element.offsetTop - 4,
    });
  };

  const peakWords = data?.peak
    ? `${data.peak.weekdayLabel} ${hourLabel(data.peak.hour)}`
    : null;

  return (
    <PanelShell
      id={PANEL_ID}
      title="Rhythm — when things get recorded"
      wide
      state={state}
      notes={notes}
      onExportCsv={onExportCsv}
      caveat="Every hour of every weekday, including the empty ones. Both axes are bucketed in EACH BUILDING'S OWN timezone, the same way a daily breakdown is — so one cell holds each building's own 14:00 rather than one instant. This counts what was RECORDED, hour by hour; a quiet hour and a quiet building are different things and this cannot tell them apart."
      emptyReason={
        data && !data.cells.length
          ? {
              title: 'The grid did not come back',
              text: 'The server returned no cells at all, which is different from a quiet period — a quiet period comes back as 168 zeros.',
            }
          : null
      }
    >
      {data ? (
        <PanelSection>
          <PanelSectionHead>
            <PanelSectionTitle>{data.label} by weekday and hour</PanelSectionTitle>
            {/* THE PEAK, IN WORDS. A ring around the darkest cell tells a reader
                where to look; only the words tell them what they are looking at,
                and the words are what survives a screenshot being described to
                somebody over the phone. */}
            {data.peak && data.max > 0 ? (
              <StatePill tone="neutral" data-testid="rhythm-peak">
                Busiest: {peakWords} — {format.withNoun(data.peak.value, data.noun)}
              </StatePill>
            ) : null}
          </PanelSectionHead>

          <SectionCaption data-testid="rhythm-hint">
            Colour is how many, scaled to the busiest cell. Hover a cell, or focus the
            grid and use the arrow keys, to read it.
          </SectionCaption>

          <ScrollableChart>
            <HeatWrap ref={wrapRef}>
              <HeatGrid
                role="grid"
                aria-label={`${data.label} by weekday and hour of day, in each building's own timezone`}
                aria-rowcount={weekdays.length + 1}
                aria-colcount={hours.length + 1}
                onKeyDown={onKeyDown}
                data-testid="rhythm-grid"
              >
                {/* the hour axis */}
                <HeatRow role="row">
                  <HeatCorner />
                  {hours.map(hour => (
                    <HeatHourLabel key={hour} role="columnheader">
                      {/* EVERY THIRD HOUR IS LABELLED. All 24 collide into a smear
                          at this column width. The CELLS are all still drawn. */}
                      {hour % 3 === 0 ? hourLabel(hour) : ''}
                    </HeatHourLabel>
                  ))}
                </HeatRow>

                {weekdays.map((day, row) => {
                  // ISO weekdays: 1 is Monday. The server states the basis on the
                  // response because Postgres has both dow (0 = Sunday) and
                  // isodow, an off-by-one that silently rotates a whole heatmap.
                  const weekday = row + 1;

                  return (
                    <HeatRow key={day} role="row">
                      <HeatDayLabel role="rowheader">{day}</HeatDayLabel>
                      {hours.map(hour => {
                        const cell = byPosition.get(`${weekday}-${hour}`);
                        const value = cell ? cell.value : 0;
                        const isPeak =
                          !!data.peak &&
                          data.max > 0 &&
                          data.peak.weekday === weekday &&
                          data.peak.hour === hour;

                        return (
                          <HeatCell
                            key={hour}
                            role="gridcell"
                            data-rc={`${row}-${hour}`}
                            data-testid="rhythm-cell"
                            data-value={value}
                            tone={heatTone(value, data.max)}
                            peak={isPeak}
                            tabIndex={cursor.row === row && cursor.col === hour ? 0 : -1}
                            aria-label={`${day} ${hourLabel(hour)}: ${
                              cell ? format.withNoun(value, data.noun) : 'no cell was returned'
                            }${isPeak ? ', the busiest cell' : ''}`}
                            onFocus={event =>
                              activate(event.currentTarget, row, hour, cell, weekday)
                            }
                            onBlur={() => setAnchor(null)}
                            onMouseEnter={event =>
                              activate(event.currentTarget, row, hour, cell, weekday)
                            }
                            onMouseLeave={() => setAnchor(null)}
                            onClick={() => onCell?.(weekday, hour)}
                          />
                        );
                      })}
                    </HeatRow>
                  );
                })}
              </HeatGrid>

              {/* ONE READOUT, DRIVEN BY HOVER AND BY FOCUS ALIKE. 168 tiny targets
                  is exactly where a mouse-only tooltip becomes unusable, so the
                  keyboard path is the same code path rather than a second one
                  that can rot. */}
              {anchor ? (
                <HeatTip
                  style={{ left: anchor.left, top: anchor.top }}
                  data-testid="rhythm-tip"
                >
                  <TipCard>
                    <TipLabel>
                      {weekdays[anchor.weekday - 1]} {hourLabel(anchor.hour)}
                    </TipLabel>
                    <TipRow>
                      <TipStrong>{anchor.value.toLocaleString()}</TipStrong>{' '}
                      {anchor.value === 1 ? data.noun[0] : data.noun[1]}
                    </TipRow>
                    <TipNote>
                      The building&apos;s own local hour, not the reader&apos;s.
                    </TipNote>
                  </TipCard>
                </HeatTip>
              ) : null}
            </HeatWrap>
          </ScrollableChart>

          {/* A GRADIENT, NOT SWATCHES. See HeatLegendBar. */}
          <HeatLegend data-testid="rhythm-legend">
            <HeatLegendLabel>0</HeatLegendLabel>
            <HeatLegendBar
              role="img"
              aria-label={`Colour scale from 0 to ${data.max.toLocaleString()} ${
                data.noun[1]
              } per cell`}
            />
            <HeatLegendLabel>
              {data.max.toLocaleString()} {data.noun[1]} in one cell
            </HeatLegendLabel>
          </HeatLegend>

          <MeterMeta data-testid="rhythm-total">
            <span>
              {format.withNoun(data.total, data.noun)} across the whole grid.
              {data.max === 0
                ? ' Every cell is zero, and the grid is still complete rather than empty.'
                : ''}
            </span>
          </MeterMeta>
        </PanelSection>
      ) : null}
    </PanelShell>
  );
};
